<template>
  <div class="sg-transfer">
    <transfer-panel
      ref="leftPanel"
      :data="sourceData"
      :option-render="optionRender"
      :title="leftPanelTitle"
      :filterable="filterable"
      :filter-method="filterMethod"
      :default-checked="leftDefaultChecked"
      :props="props"
      :noDataText="noDataText"
      :listHeight="listHeight"
      :listWidth="listWidth"
      @checked-change="onSourceCheckedChange"
      @on-input="handelInput"
      @on-clear="handelClear"
    >
    </transfer-panel>
    <div class="sg-transfer__buttons">
      <sg-button
        type="primary"
        size="small"
        :class="['sg-transfer__button']"
        :disabled="rightChecked.length === 0"
        @click="addToLeft"
      >
        <sg-icon ><ArrowLeft /></sg-icon>
      </sg-button>
      <sg-button
        type="primary"
        size="small"
        :class="['sg-transfer__button']"
        :disabled="leftChecked.length === 0"
        @click="addToRight"
      >

        <sg-icon ><ArrowRight /></sg-icon>
      </sg-button>
    </div>
    <transfer-panel
      ref="rightPanel"
      :data="targetData"
      :option-render="optionRender"
      :filterable="filterable"
      :filter-method="filterMethod"
      :title="rightPanelTitle"
      :default-checked="rightDefaultChecked"
      :props="props"
      :noDataText="noDataText"
      :listHeight="listHeight"
      :listWidth="listWidth"
      @checked-change="onTargetCheckedChange"
      @on-input="handelInput"
      @on-clear="handelClear"
    >
    </transfer-panel>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  h,
  reactive,
  ref,
  toRefs,
  watch
} from 'vue'
import SgButton from '@sgui-plus/components/button'
import SgIcon from '@sgui-plus/components/icon'
import { useLocale, useFormItem } from '@sgui-plus/hooks'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { debugWarn } from '@sgui-plus/utils/error'
import {  ArrowLeft, ArrowRight, } from '@sgui-plus/icons-vue'
import TransferPanel from './transfer-panel.vue'
import { useComputedData } from './useComputedData'
import {
  useCheckedChange,
  LEFT_CHECK_CHANGE_EVENT,
  RIGHT_CHECK_CHANGE_EVENT,
} from './useCheckedChange'
import { useMove } from './useMove'
import type { PropType, VNode } from 'vue'
import type { DataItem, Key, Props, TargetOrder } from './transfer'

type TransferType = InstanceType<typeof TransferPanel>

export default defineComponent({
  name: 'SgTransfer',

  components: {
    TransferPanel,
    SgButton,
    SgIcon,
    ArrowLeft,
    ArrowRight,
  },

  props: {
    data: {
      type: Array as PropType<DataItem[]>,
      default: () => [],
    },
    titles: {
      type: Array as PropType<any> as PropType<[string, string]>,
      default: () => [],
    },
    filterMethod: Function as PropType<
      (query: string, item: DataItem) => boolean
    >,
    leftDefaultChecked: {
      type: Array as PropType<Key[]>,
      default: () => [],
    },
    rightDefaultChecked: {
      type: Array as PropType<Key[]>,
      default: () => [],
    },
    renderContent: Function as PropType<(h, option) => VNode>,
    modelValue: {
      type: Array as PropType<Key[]>,
      default: () => [],
    },
    filterable: {
      type: Boolean,
      default: false,
    },
    props: {
      type: Object as PropType<Props>,
      default: () => ({
        label: 'label',
        key: 'key',
        disabled: 'disabled',
      }),
    },
    targetOrder: {
      type: String as PropType<TargetOrder>,
      default: 'original',
      validator: (val: string) => {
        return ['original', 'push', 'unshift'].includes(val)
      },
    },
    validateEvent: {
      type: Boolean,
      default: true,
    },
    noDataText: String,
    listHeight: Number,
    listWidth: Number
  },

  emits: [
    UPDATE_MODEL_EVENT,
    'on-change',
    LEFT_CHECK_CHANGE_EVENT,
    RIGHT_CHECK_CHANGE_EVENT,
    'on-query-change',
    'on-query-clear'
  ],

  setup(props, { emit, slots }) {
    const { t } = useLocale()
    const { formItem } = useFormItem()

    const checkedState = reactive({
      leftChecked: [],
      rightChecked: [],
    })

    const { propsKey, sourceData, targetData } = useComputedData(props)

    const { onSourceCheckedChange, onTargetCheckedChange } = useCheckedChange(
      checkedState,
      emit
    )

    const { addToLeft, addToRight } = useMove(
      props,
      checkedState,
      propsKey,
      emit
    )

    const leftPanel = ref<TransferType>()
    const rightPanel = ref<TransferType>()

    const clearFilter = (which: 'left' | 'right') => {
      switch (which) {
        case 'left':
          leftPanel.value!.query = ''
          break
        case 'right':
          rightPanel.value!.query = ''
          break
      }
    }

    const moveTo = (direction: 'left' | 'right') => {
      direction === 'left' ? addToLeft() : addToRight()
    }


    const leftPanelTitle = computed(
      () => props.titles[0] || t('sg.transfer.titles.0')
    )

    const rightPanelTitle = computed(
      () => props.titles[1] || t('sg.transfer.titles.1')
    )

    const optionRender = computed(() => (option) => {
      if (props.renderContent) return props.renderContent(h, option)

      if (slots.default) return slots.default({ option })

      return h('span', option[props.props.label] || option[props.props.key])
    })

    const handelInput = (value) => {
        emit('on-query-change', value)
    }

    const handelClear = () => {
      emit('on-query-clear')
    }

    watch(
      () => props.modelValue,
      () => {
        if (props.validateEvent) {
          formItem?.validate?.('change').catch((err) => debugWarn(err))
        }
      }
    )

    return {
      sourceData,
      targetData,
      onSourceCheckedChange,
      onTargetCheckedChange,
      addToLeft,
      addToRight,

      ...toRefs(checkedState),
      leftPanelTitle,
      rightPanelTitle,
      clearFilter,
      moveTo,
      leftPanel,
      rightPanel,

      optionRender,

      handelInput,
      handelClear,
    }
  },
})
</script>
